// Measurement overlay — draws the width + height measurement lines Unity
// projects for the current setup, as an SVG laid over the viewer canvas.
//
// Unity sends screen-space endpoints (CSS px, top-left origin) relative to its
// own render resolution (payload.viewport). Our overlay element fills the
// viewport container, which may be a different pixel size (devicePixelRatio,
// CSS scaling, fit-to-container canvas), so we scale every point by
// overlaySize ÷ viewport. We measure the overlay's own client size with a
// ResizeObserver so the lines stay aligned as the container resizes.
//
// The component is a pure renderer of the last payload it was handed — the tool
// stashes the latest "SetupMeasurement" event into state and passes it here.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MeasurementLine, MeasurementSetup } from "../measurement";

export interface MeasurementOverlayProps {
  /** The latest "setup" payload, or null when there's nothing to draw (scene
   *  empty / measurements toggled off). */
  setup: MeasurementSetup | null;
  /** Convert a line's metres to the label string. Defaults to cm, 1 decimal
   *  (e.g. 2.4 m → "240.0 cm"). Pass a converter to show other units. */
  formatLength?: (meters: number) => string;
}

const defaultFormat = (meters: number) => `${(meters * 100).toFixed(1)} cm`;

export function MeasurementOverlay({ setup, formatLength = defaultFormat }: MeasurementOverlayProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Track the overlay's own pixel size so we can scale Unity's viewport-relative
  // coords onto it. ResizeObserver fires on container resize (panel collapse,
  // window resize, mobile sheet) without a manual listener.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!setup) {
    // Keep the measuring element mounted (so the ResizeObserver stays live and
    // size is ready the instant a setup arrives) but render no lines.
    return <div ref={ref} className="sd-measure-overlay" aria-hidden="true" />;
  }

  const vw = setup.viewport.width || 1;
  const vh = setup.viewport.height || 1;
  const sx = size.w / vw;
  const sy = size.h / vh;

  // Setup centre in overlay pixels — the average of every visible endpoint. We
  // push each label away from this point so a line on the right gets its label
  // to the right, a top edge gets it above, etc. (The two lines share the near
  // corner and span the bounding box, so their midpoint is a good "inside".)
  const pts: { x: number; y: number }[] = [];
  if (setup.widthLine.onScreen) {
    pts.push({ x: setup.widthLine.from.x * sx, y: setup.widthLine.from.y * sy });
    pts.push({ x: setup.widthLine.to.x * sx, y: setup.widthLine.to.y * sy });
  }
  if (setup.heightLine.onScreen) {
    pts.push({ x: setup.heightLine.from.x * sx, y: setup.heightLine.from.y * sy });
    pts.push({ x: setup.heightLine.to.x * sx, y: setup.heightLine.to.y * sy });
  }
  const center =
    pts.length > 0
      ? {
          x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
          y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
        }
      : { x: 0, y: 0 };

  return (
    <div ref={ref} className="sd-measure-overlay" aria-hidden="true">
      <svg className="sd-measure-overlay__svg" width="100%" height="100%">
        <MeasureLine line={setup.widthLine} sx={sx} sy={sy} center={center} label={formatLength(setup.widthLine.meters)} />
        <MeasureLine line={setup.heightLine} sx={sx} sy={sy} center={center} label={formatLength(setup.heightLine.meters)} />
      </svg>
    </div>
  );
}

function MeasureLine({
  line,
  sx,
  sy,
  center,
  label,
}: {
  line: MeasurementLine;
  sx: number;
  sy: number;
  center: { x: number; y: number };
  label: string;
}) {
  const textRef = useRef<SVGTextElement>(null);
  const [textW, setTextW] = useState(0);

  // Measure the rendered label width so the dark tag rect fits it exactly.
  // useLayoutEffect so the rect is sized before paint (no one-frame flash).
  useLayoutEffect(() => {
    if (textRef.current) setTextW(textRef.current.getComputedTextLength());
  }, [label, sx, sy]);

  // Either endpoint behind the camera → the projection is meaningless, skip.
  if (!line.onScreen) return null;

  const ax = line.from.x * sx;
  const ay = line.from.y * sy;
  const bx = line.to.x * sx;
  const by = line.to.y * sy;

  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2;

  // Label offset direction. Unity now tells us the side ("top"/"bottom"/
  // "left"/"right") so we push straight that way — exact and camera-agnostic.
  // Older builds omit `side`; then fall back to the line normal pointing away
  // from the setup centre (label lands outside the bounding box either way).
  let ux: number;
  let uy: number;
  if (line.side) {
    // "side" is the side of the LINE the setup is on, so the label goes the
    // opposite way to land outside the bounding box: side "right" → push left,
    // "left" → push right. (top/bottom already read correctly.)
    const dir = { top: [0, -1], bottom: [0, 1], left: [1, 0], right: [-1, 0] }[line.side];
    ux = dir[0];
    uy = dir[1];
  } else {
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    ux = -dy / len;
    uy = dx / len;
    if (ux * (mx - center.x) + uy * (my - center.y) < 0) {
      ux = -ux;
      uy = -uy;
    }
  }

  // Dark tag dimensions — width padded around the measured text; fixed height
  // sized for the ~12px label plus vertical padding.
  const padX = 6;
  const tagH = 18;
  const tagW = textW + padX * 2;

  // Offset the tag CENTRE far enough that the tag clears the line on the chosen
  // side: GAP from the line to the tag's near edge, plus half the tag's extent
  // along the offset axis. So a left/right label sits fully beside the line
  // (offset by ~the label's own width), a top/bottom label fully above/below.
  const GAP = 8; // px between the line and the nearest edge of the tag
  const halfExtent = (Math.abs(ux) * tagW + Math.abs(uy) * tagH) / 2;
  const offset = GAP + halfExtent;
  const lx = mx + ux * offset;
  const ly = my + uy * offset;

  return (
    <g className="sd-measure-overlay__line">
      <line x1={ax} y1={ay} x2={bx} y2={by} />
      {/* End caps — short dots so the measured span reads clearly. */}
      <circle cx={ax} cy={ay} r={3} className="sd-measure-overlay__cap" />
      <circle cx={bx} cy={by} r={3} className="sd-measure-overlay__cap" />
      {/* White text on a dark rounded tag, centred on the offset point. */}
      <rect
        className="sd-measure-overlay__tag"
        x={lx - tagW / 2}
        y={ly - tagH / 2}
        width={tagW}
        height={tagH}
        rx={4}
      />
      <text
        ref={textRef}
        x={lx}
        y={ly + 0.5}
        className="sd-measure-overlay__label"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {label}
      </text>
    </g>
  );
}
